/**
 * Acquisition attribution — silent, first-touch capture.
 *
 * Answers "where did this signup actually come from?" without asking
 * the user anything. On the FIRST page of a visit we snapshot the utm
 * parameters, the referrer and the landing page into sessionStorage;
 * `Signup` replays that snapshot with the registration call, so a user
 * who lands on an ad, reads three pages and only then creates an
 * account still carries the campaign that brought them in.
 *
 * Design rules
 * ────────────
 *  - FIRST TOUCH WINS. Once the snapshot exists for the session it is
 *    never overwritten. A user who lands on `?utm_campaign=reprise-mai`
 *    and later clicks an internal link with its own utm tags is still
 *    attributed to the campaign that acquired them.
 *  - sessionStorage, not localStorage. Attribution describes THIS
 *    visit. A localStorage copy would still be there in three months
 *    and would credit an old campaign for a fresh, direct signup.
 *  - Never throws. Safari private mode and hardened browsers make
 *    storage access raise; attribution is a nice-to-have, so every
 *    access is guarded and a failure silently yields null.
 *  - No PII. utm tags, referring host and our own path — nothing the
 *    user typed.
 *
 * The complementary DECLARED channel (the "Comment as-tu connu Archer ?"
 * dropdown) is separate: it's a form field on the signup page, sent
 * alongside this snapshot as `acquisition_source`.
 */

const STORAGE_KEY = 'archer_attribution';

// Mirrors the caps enforced server-side in routes/auth_routes.py, so a
// hostile query string can't bloat the request either.
const UTM_MAX = 120;
const URL_MAX = 400;

function clean(value, limit) {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  return trimmed.slice(0, limit);
}

/** document.referrer, minus our own pages — an internal navigation is
 *  not an acquisition source. Returns null for direct visits. */
function externalReferrer() {
  try {
    const ref = document.referrer;
    if (!ref) return null;
    if (new URL(ref).origin === window.location.origin) return null;
    return clean(ref, URL_MAX);
  } catch (_) {
    return null;
  }
}

/**
 * Snapshot the current page as the session's acquisition context — but
 * only if nothing has been captured yet. Safe to call on every route
 * change; only the first call in a session writes anything.
 *
 * @returns {object|null} the stored snapshot, or null if storage is unavailable
 */
export function captureAttribution() {
  if (typeof window === 'undefined') return null;
  try {
    const existing = window.sessionStorage.getItem(STORAGE_KEY);
    if (existing) return JSON.parse(existing);

    const params = new URLSearchParams(window.location.search);
    const snapshot = {
      utm_source: clean(params.get('utm_source'), UTM_MAX),
      utm_medium: clean(params.get('utm_medium'), UTM_MAX),
      utm_campaign: clean(params.get('utm_campaign'), UTM_MAX),
      // `?ref=` — le raccourci des liens qu'on ne contrôle pas. Un article
      // de presse ou un influenceur pose rarement trois paramètres utm
      // propres ; il colle ce qu'on lui a donné. Sans ce champ, ces
      // visites tombent en « direct » et la campagne de lancement
      // ressemble à du hasard.
      ref: clean(params.get('ref'), UTM_MAX),
      referrer: externalReferrer(),
      // Path + query only: the origin is us, and storing the full URL
      // would just repeat it on every row.
      landing_page: clean(window.location.pathname + window.location.search, URL_MAX),
    };
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
    return snapshot;
  } catch (_) {
    // Private mode / disabled storage / malformed JSON — attribution is
    // optional by construction, so we degrade to "unknown source".
    return null;
  }
}

/**
 * Read back the session's attribution snapshot for the signup payload.
 *
 * @returns {object|null} the snapshot, or null when nothing was captured
 *                        (storage blocked, or the tab was opened straight
 *                        onto /signup before capture ran)
 */
export function getAttribution() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (_) {
    return null;
  }
}
